import { useMemo } from 'react'

import { ANIMATION_DURATION, chartColors } from '../consts'
import type { ChartData } from '../types'

type UseChartSeriesParams = {
  data: ChartData[]
  appearance: 'light' | 'dark'
  chartType: 'bar' | 'line'
}

export const useChartSeries = ({ data, appearance, chartType }: UseChartSeriesParams) => {
  const colors = useMemo(() => {
    return chartColors[appearance].series
  }, [appearance])

  const series = useMemo(() => {
    return data.map((serie, serieIndex) => {
      if (chartType === 'bar') {
        return {
          name: serie.label,
          type: chartType,
          data: serie.data.map((dataPoint) => ({
            value: dataPoint.value,
            itemStyle: {
              color: [colors.primary, colors.secondary][serieIndex],
              borderRadius: dataPoint.value >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4],
            },
          })),
          emphasis: {
            focus: 'series',
          },
          animationEasing: 'cubicOut',
          animationDuration: ANIMATION_DURATION,
          barWidth: '20%', // TODO: make it dependent on number of bars
        }
      }

      const baseSeries = {
        name: serie.label,
        type: chartType,
        data: serie.data.map((dataPoint) => dataPoint.value),
        emphasis: {
          focus: 'series',
          ...(chartType === 'line' && { scale: true }),
        },
        animationEasing: 'cubicOut',
        animationDuration: ANIMATION_DURATION,
      }

      if (chartType === 'line') {
        const opacity = Math.round(0.15 * 255)
          .toString(16)
          .padStart(2, '0')

        return {
          ...baseSeries,
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: serie.data.length === 1,
          lineStyle: {
            color: [colors.primary, colors.secondary][serieIndex],
            width: 2,
            type: ['solid', [4]][serieIndex],
            zIndex: [1, 0][serieIndex],
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: `${[colors.areaPrimary, colors.areaSecondary][serieIndex]}${opacity}`,
                },
                { offset: 1, color: `${[colors.areaPrimary, colors.areaSecondary][serieIndex]}00` },
              ],
            },
          },
          itemStyle: { color: [colors.primary, colors.secondary][serieIndex] },
        }
      }

      return baseSeries
    })
  }, [data, chartType, colors])

  return { series }
}
